import { Component, DestroyRef, Input, OnInit } from '@angular/core';
import { CommentImportance } from '../../../core/models/freight-document/CommentImportance';
import { CommentEventType } from '../../../core/models/freight-document/CommentEventType';
import { BehaviorSubject } from 'rxjs';
import { WithObservationForm } from '../../../core/models/forms/WithObservationForm';
import { MultiSelectData, MultiselectOption } from '../multiselect/multiselect.component'
import { CommentImportancePipe } from '../pipes/comment-importance.pipe'
import { EventTypePipe } from '../pipes/event-type.pipe'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'

@Component({
  selector: 'app-search-with-observations',
  template: `
    <div class="row g-0 align-items-center" [formGroup]="form">
      <div class="col-auto me-5">
        <input type="checkbox" formControlName="onlyWithComments" id="only-with-comments"/>
        <label for="only-with-comments" class="mb-0">{{ 'observations.withObservations' | transloco }}</label>
      </div>

      <div class="col filters-container">
        <div class="type-dropdown">
          <label class="mb-0 me-2">{{ 'observations.observationType' | transloco }}</label>
          <app-multiselect class="w-100" [badgeLimit]="2" [disabled]="disabled | async" [data]="importanceDropdownData"></app-multiselect>
        </div>

        <div class="event-dropdown">
          <label class="mb-0 me-2">{{ 'observations.observationEvent' | transloco }}</label>
          <app-multiselect class="w-100" [badgeLimit]="2" [disabled]="disabled | async" [data]="eventDropdownData"></app-multiselect>
        </div>
      </div>
    </div>
  `,
  styleUrls: ['./search-with-observations.component.scss'],
  providers: [CommentImportancePipe, EventTypePipe]
})
export class SearchWithObservationsComponent implements OnInit {
  @Input() form: WithObservationForm;
  @Input() importanceSelectedItems: CommentImportance[] = [];
  @Input() eventSelectedItems: CommentEventType[] = [];

  protected importanceDropdownData = new MultiSelectData();
  protected eventDropdownData = new MultiSelectData();
  protected disabled = new BehaviorSubject(true);

  constructor(private commentImportancePipe: CommentImportancePipe,
              private eventTypePipe: EventTypePipe,
              private destroyRef: DestroyRef) {}

  ngOnInit(): void {
    this.initValues();
    this.initSubscriptions();
  }

  private initValues(): void {
    this.importanceDropdownData.options.push(...this.getImportanceDropdownOptions(Object.values(CommentImportance)));
    this.importanceDropdownData.control.setValue(this.getImportanceDropdownOptions(this.importanceSelectedItems))

    this.eventDropdownData.options.push(...this.getEventDropdownOptions(Object.values(CommentEventType)));
    this.eventDropdownData.control.setValue(this.getEventDropdownOptions(this.eventSelectedItems));

    this.disabled.next(!this.form.onlyWithComments.value);
  }

  private initSubscriptions(): void {
    this.form.onlyWithComments.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        this.disabled.next(!value);
        if (!value) {
          this.importanceDropdownData.control.reset();
          this.eventDropdownData.control.reset();
        }
      });

    this.importanceDropdownData.control.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        // rework form control chain to drop DropdownList type and change this mapping
        this.form.commentImportance.setValue(value?.map(v => ({ id: Object.values(CommentImportance)[v.id] })) || []);
      });

    this.eventDropdownData.control.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        // rework form control chain to drop DropdownList type and change this mapping
        this.form.commentEventType.setValue(value?.map(v => ({ id: Object.values(CommentEventType)[v.id] })) || []);
      });
  }

  private getImportanceDropdownOptions(dropdownOptions: CommentImportance[]): MultiselectOption[] {
    return dropdownOptions.map(option => (
      {
        id: Object.values(CommentImportance).indexOf(option),
        label: this.commentImportancePipe.transform(option)
      }
    ) as MultiselectOption);
  }

  private getEventDropdownOptions(dropdownOptions: CommentEventType[]): MultiselectOption[] {
    return dropdownOptions.map(option => (
      {
        id: Object.values(CommentEventType).indexOf(option),
        label: this.eventTypePipe.transform(option)
      }
    ) as MultiselectOption);
  }
}
